import React from "react";
import { motion } from "framer-motion";
import { FiTarget, FiEye, FiHeart } from "react-icons/fi";
import { Sparkles, ShieldCheck } from "lucide-react";

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const cardsData = [
    {
      title: "Our Mission",
      description:
        "Our mission is to offer innovative technologies and services that allow logistics companies to track, analyze, and reduce their carbon emissions. We are dedicated to empowering businesses to adopt more sustainable practices and drive the shift towards a zero-carbon future.",
      icon: FiTarget,
      accent: "from-emerald-500 to-teal-600",
    },
    {
      title: "Our Vision",
      description:
        "Our vision is to lead in green logistics, offering innovative solutions that reduce emissions and help businesses thrive in an eco-conscious world. We aim to transform the logistics industry through technology, sustainable partnerships, and environmental stewardship.",
      icon: FiEye,
      accent: "from-teal-500 to-emerald-600",
    },
    {
      title: "Our Values",
      description:
        "We believe in integrity, innovation, and collaboration. Our values drive us to deliver transparent, effective solutions that support our clients and a sustainable planet. We're committed to trust and creativity, working with partners to inspire lasting environmental change in logistics.",
      icon: FiHeart,
      accent: "from-emerald-600 to-teal-500",
    },
  ];

  return (
    <motion.section
      id="about"
      className="px-6 py-24 bg-slate-50 relative overflow-hidden font-sans text-slate-900"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={containerVariants}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Pill Header */}
        <div className="flex flex-col items-center text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100/80 border border-emerald-300 px-4 py-1.5 rounded-full mb-4 shadow-sm">
            <Sparkles className="w-4 h-4 text-emerald-700" />
            <span className="text-xs sm:text-sm font-bold text-emerald-900 tracking-wide uppercase">
              Our Story & Purpose
            </span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            About <span className="text-[#0E3B2E]">Pure Prakruti</span>
          </h2>

          <p className="text-base sm:text-xl text-slate-600 font-medium max-w-4xl mt-4 leading-relaxed">
            Pure Prakruti is your partner in building a sustainable future. Focused on the road logistics industry—responsible for 27% of global emissions—we provide tools and insights to help businesses cut their carbon footprint and drive real climate action.
          </p>
        </div>

        {/* 3 Modern Value Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {cardsData.map((card, idx) => {
            const IconComp = card.icon;
            return (
              <motion.div
                key={idx}
                className="bg-white border border-slate-200/90 rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between relative overflow-hidden group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * idx }}
              >
                {/* Top Accent Line */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${card.accent}`} />

                <div>
                  <div className="w-14 h-14 rounded-2xl bg-[#0E3B2E] text-emerald-400 flex items-center justify-center mb-6 shadow-md group-hover:scale-110 transition-transform">
                    <IconComp className="w-7 h-7" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">
                    {card.title}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-medium">
                    {card.description}
                  </p>
                </div>

                <div className="mt-8 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs font-bold text-emerald-800 uppercase tracking-wider">
                  <ShieldCheck className="w-4 h-4 text-emerald-700" /> Sustainable Freight Standard
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </motion.section>
  );
};

export default About;
